import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  Row,
  Col,
  theme,
  Spin,
  Empty,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Tooltip,
  Divider,
  Descriptions,
} from "antd";
import {
  ThunderboltOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  CopyOutlined,
  EyeOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import { authenticatedFetch } from "@/libs/api";

const { Title, Text } = Typography;

// FCAS Market types
const FCAS_MARKETS = [
  { value: "RAISE_1S", label: "Raise 1 Second", description: "1 second raise contingency" },
  { value: "RAISE_6S", label: "Raise 6 Second", description: "6 second raise contingency" },
  { value: "RAISE_60S", label: "Raise 60 Second", description: "60 second raise contingency" },
  { value: "RAISE_5MIN", label: "Raise 5 Minute", description: "5 minute raise regulation" },
  { value: "LOWER_1S", label: "Lower 1 Second", description: "1 second lower contingency" },
  { value: "LOWER_6S", label: "Lower 6 Second", description: "6 second lower contingency" },
  { value: "LOWER_60S", label: "Lower 60 Second", description: "60 second lower contingency" },
  { value: "LOWER_5MIN", label: "Lower 5 Minute", description: "5 minute lower regulation" },
];

// Bid strategies
const BID_STRATEGIES = [
  { value: "FIXED_PRICE", label: "Fixed Price", description: "Bid at a fixed price point" },
  { value: "PRICE_TAKER", label: "Price Taker", description: "Bid at $0 to always be dispatched" },
  { value: "DYNAMIC", label: "Dynamic", description: "Adjust price based on market conditions" },
  { value: "AVAILABILITY_BASED", label: "Availability Based", description: "Bid based on available capacity" },
];

// Capacity calculation methods
const CAPACITY_METHODS = [
  { value: "HEADROOM", label: "Headroom", description: "Available charge capacity (max - current)" },
  { value: "FOOTROOM", label: "Footroom", description: "Available discharge capacity (current - min)" },
  { value: "FIXED_KW", label: "Fixed kW", description: "Fixed capacity value in kW" },
  { value: "PCT_AVAILABLE", label: "% Available", description: "Percentage of total available capacity" },
  { value: "SOC_BASED", label: "SOC Based", description: "Capacity calculated from state of charge" },
];

// API helper
const apiCall = async (url, options = {}) => {
  const response = await authenticatedFetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  return response.json();
};

export default function BiddingRules() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [pageSize, setPageSize] = useState(20);

  // Modal states
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [viewingRule, setViewingRule] = useState(null);
  const [form] = Form.useForm();

  // Watch form values for conditional rendering
  const bidStrategy = Form.useWatch("bid_strategy", form);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall("/api/bidding/rules");
      if (data.success) {
        setRules(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch rules:", error);
      message.error("Failed to load bidding rules");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleAddRule = () => {
    setEditingRule(null);
    form.resetFields();
    form.setFieldsValue({
      bid_strategy: "AVAILABILITY_BASED",
      capacity_method: "HEADROOM",
      min_soc_pct: 20,
      max_soc_pct: 90,
    });
    setRuleModalVisible(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    form.setFieldsValue({
      ...rule,
      bid_params: rule.bid_params_json ? JSON.stringify(rule.bid_params_json, null, 2) : "",
    });
    setRuleModalVisible(true);
  };

  const handleDuplicateRule = (rule) => {
    setEditingRule(null);
    form.setFieldsValue({
      ...rule,
      rule_id: undefined,
      rule_name: `${rule.rule_name || "Rule"} (Copy)`,
      bid_params: rule.bid_params_json ? JSON.stringify(rule.bid_params_json, null, 2) : "",
    });
    setRuleModalVisible(true);
  };

  const handleViewRule = (rule) => {
    setViewingRule(rule);
    setDetailModalVisible(true);
  };

  const handleDeleteRule = async (ruleId) => {
    Modal.confirm({
      title: "Delete Bidding Rule",
      content: "Are you sure you want to delete this bidding rule? This will affect all control groups using this rule.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const data = await apiCall(`/api/bidding/rules/${ruleId}`, { method: "DELETE" });
          if (data.success) {
            message.success("Rule deleted successfully");
            fetchRules();
          } else {
            message.error(data.message || "Failed to delete rule");
          }
        } catch (error) {
          message.error("Failed to delete rule");
        }
      },
    });
  };

  const handleSaveRule = async () => {
    try {
      const values = await form.validateFields();

      // Parse bid_params JSON
      let bidParams = {};
      if (values.bid_params) {
        try {
          bidParams = JSON.parse(values.bid_params);
        } catch (e) {
          message.error("Invalid JSON in bid parameters");
          return;
        }
      }

      const payload = {
        ...values,
        bid_params_json: bidParams,
      };
      delete payload.bid_params;

      let data;
      if (editingRule) {
        data = await apiCall(`/api/bidding/rules/${editingRule.rule_id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        data = await apiCall("/api/bidding/rules", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      if (data.success) {
        message.success(editingRule ? "Rule updated successfully" : "Rule created successfully");
        setRuleModalVisible(false);
        fetchRules();
      } else {
        message.error(data.message || "Failed to save rule");
      }
    } catch (error) {
      if (!error.errorFields) {
        message.error("Failed to save rule: " + error.message);
      }
    }
  };

  const columns = [
    {
      title: "Rule Name",
      key: "name",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.rule_name || record.rule_id}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.rule_id}</Text>
        </Space>
      ),
    },
    {
      title: "FCAS Markets",
      dataIndex: "fcas_markets",
      key: "fcas_markets",
      width: 200,
      render: (markets) => {
        // Handle both array and single value for backwards compatibility
        const marketList = Array.isArray(markets) ? markets : (markets ? [markets] : []);
        return (
          <Space size={[0, 4]} wrap>
            {marketList.map(market => {
              const m = FCAS_MARKETS.find((f) => f.value === market);
              const isRaise = market?.includes("RAISE");
              return (
                <Tag key={market} color={isRaise ? "green" : "orange"} style={{ margin: 0 }}>
                  {m?.label || market}
                </Tag>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: "Strategy",
      dataIndex: "bid_strategy",
      key: "bid_strategy",
      render: (strategy) => {
        const s = BID_STRATEGIES.find((b) => b.value === strategy);
        return (
          <Tooltip title={s?.description}>
            <Tag color="blue">{s?.label || strategy}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Capacity Method",
      dataIndex: "capacity_method",
      key: "capacity_method",
      render: (method) => {
        const m = CAPACITY_METHODS.find((c) => c.value === method);
        return (
          <Tooltip title={m?.description}>
            <Tag color="purple">{m?.label || method}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Capacity Range",
      key: "capacity",
      render: (_, record) => (
        <span>
          {record.min_bid_kw || 0} - {record.max_bid_kw || "∞"} kW
        </span>
      ),
    },
    {
      title: "SOC Range",
      key: "soc",
      render: (_, record) => (
        <span>
          {record.min_soc_pct || 0}% - {record.max_soc_pct || 100}%
        </span>
      ),
    },
    {
      title: "Groups Using",
      dataIndex: "group_count",
      key: "groups",
      align: "center",
      render: (count) => <Tag>{count || 0}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} size="small" onClick={() => handleViewRule(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} size="small" onClick={() => handleEditRule(record)} />
          </Tooltip>
          <Tooltip title="Duplicate">
            <Button type="text" icon={<CopyOutlined />} size="small" onClick={() => handleDuplicateRule(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button type="text" icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteRule(record.rule_id)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <PageLayout active="biddingRules">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="biddingRules">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 8 }}>
          <SettingOutlined style={{ marginRight: 12, color: token.colorPrimary }} />
          Bidding Rules
        </Title>
        <Text type="secondary">
          Create and manage FCAS bidding rules. Rules define how capacity is calculated and bid prices are determined for each FCAS market.
        </Text>
      </div>

      {/* Rules Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRule}>
            Create Bidding Rule
          </Button>
        }
      >
        <Table
          dataSource={rules}
          columns={columns}
          rowKey="rule_id"
          pagination={makePagination(pageSize, setPageSize)}
          locale={{ emptyText: <Empty description="No bidding rules created yet" /> }}
        />
      </Card>

      {/* Create/Edit Rule Modal */}
      <Modal
        title={editingRule ? "Edit Bidding Rule" : "Create Bidding Rule"}
        open={ruleModalVisible}
        onCancel={() => setRuleModalVisible(false)}
        onOk={handleSaveRule}
        okText={editingRule ? "Update" : "Create"}
        width={700}
        styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="rule_name"
                label="Rule Name"
                rules={[{ required: true, message: "Please enter a rule name" }]}
              >
                <Input placeholder="e.g., NSW Raise 6S Standard" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="fcas_markets"
                label="FCAS Markets"
                rules={[{ required: true, message: "Please select at least one FCAS market" }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select FCAS markets"
                  options={FCAS_MARKETS.map(m => ({ value: m.value, label: m.label }))}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="bid_strategy"
                label={
                  <Space>
                    Bid Strategy
                    <Tooltip title="How to determine bid price">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                rules={[{ required: true }]}
              >
                <Select
                  options={BID_STRATEGIES.map(s => ({
                    value: s.value,
                    label: (
                      <Space direction="vertical" size={0}>
                        <span>{s.label}</span>
                        <Text type="secondary" style={{ fontSize: 11 }}>{s.description}</Text>
                      </Space>
                    ),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="capacity_method"
                label={
                  <Space>
                    Capacity Method
                    <Tooltip title="How to calculate available bid capacity">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                rules={[{ required: true }]}
              >
                <Select
                  options={CAPACITY_METHODS.map(m => ({
                    value: m.value,
                    label: (
                      <Space direction="vertical" size={0}>
                        <span>{m.label}</span>
                        <Text type="secondary" style={{ fontSize: 11 }}>{m.description}</Text>
                      </Space>
                    ),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          {bidStrategy === "FIXED_PRICE" && (
            <Form.Item
              name="fixed_price"
              label="Fixed Bid Price ($/MWh)"
              rules={[{ required: true, message: "Required for fixed price strategy" }]}
            >
              <InputNumber style={{ width: "100%" }} min={0} max={15000} placeholder="e.g., 300" />
            </Form.Item>
          )}

          <Divider>Capacity Limits</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="min_bid_kw" label="Min Bid Capacity (kW)">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="max_bid_kw" label="Max Bid Capacity (kW)">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="No limit" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="reserve_kw"
                label={
                  <Space>
                    Reserve (kW)
                    <Tooltip title="Capacity to hold back from bidding">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
              >
                <InputNumber style={{ width: "100%" }} min={0} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>SOC Constraints</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="min_soc_pct" label="Min SOC (%)">
                <InputNumber style={{ width: "100%" }} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="max_soc_pct" label="Max SOC (%)">
                <InputNumber style={{ width: "100%" }} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="soc_headroom_pct"
                label={
                  <Space>
                    SOC Headroom (%)
                    <Tooltip title="Additional SOC buffer for FCAS response">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
              >
                <InputNumber style={{ width: "100%" }} min={0} max={50} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Advanced Parameters</Divider>

          <Form.Item
            name="bid_params"
            label={
              <Space>
                Bid Parameters (JSON)
                <Tooltip title="Strategy-specific parameters including AEMO 10 price bands">
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
            extra='Example: {"price_bands": [{"price": 0, "capacity_pct": 50}, {"price": 300, "capacity_pct": 100}]}'
          >
            <Input.TextArea
              rows={4}
              placeholder='{"price_bands": [{"price": 0, "capacity_pct": 100}]}'
              style={{ fontFamily: "monospace" }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Rule Detail Modal */}
      <Modal
        title="Bidding Rule Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          <Button key="edit" type="primary" onClick={() => { setDetailModalVisible(false); handleEditRule(viewingRule); }}>
            Edit Rule
          </Button>,
        ]}
        width={600}
      >
        {viewingRule && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Rule ID" span={2}>{viewingRule.rule_id}</Descriptions.Item>
            <Descriptions.Item label="Rule Name" span={2}>{viewingRule.rule_name}</Descriptions.Item>
            <Descriptions.Item label="FCAS Markets" span={2}>
              <Space size={[4, 4]} wrap>
                {(Array.isArray(viewingRule.fcas_markets) ? viewingRule.fcas_markets : [viewingRule.fcas_markets]).filter(Boolean).map(market => {
                  const m = FCAS_MARKETS.find(f => f.value === market);
                  const isRaise = market?.includes("RAISE");
                  return <Tag key={market} color={isRaise ? "green" : "orange"}>{m?.label || market}</Tag>;
                })}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Bid Strategy">{BID_STRATEGIES.find(s => s.value === viewingRule.bid_strategy)?.label}</Descriptions.Item>
            <Descriptions.Item label="Capacity Method">{CAPACITY_METHODS.find(m => m.value === viewingRule.capacity_method)?.label}</Descriptions.Item>
            <Descriptions.Item label="Reserve">{viewingRule.reserve_kw || 0} kW</Descriptions.Item>
            <Descriptions.Item label="Min Bid">{viewingRule.min_bid_kw || 0} kW</Descriptions.Item>
            <Descriptions.Item label="Max Bid">{viewingRule.max_bid_kw || "No limit"} kW</Descriptions.Item>
            <Descriptions.Item label="Min SOC">{viewingRule.min_soc_pct || 0}%</Descriptions.Item>
            <Descriptions.Item label="Max SOC">{viewingRule.max_soc_pct || 100}%</Descriptions.Item>
            <Descriptions.Item label="SOC Headroom">{viewingRule.soc_headroom_pct || 0}%</Descriptions.Item>
            <Descriptions.Item label="Groups Using">{viewingRule.group_count || 0} control groups</Descriptions.Item>
            <Descriptions.Item label="Bid Parameters" span={2}>
              <pre style={{ margin: 0, fontSize: 11, maxHeight: 150, overflow: "auto" }}>
                {JSON.stringify(viewingRule.bid_params_json || {}, null, 2)}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </PageLayout>
  );
}
